import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { CalendarViews } from './types';

export function CalendarHeaderViewSwitch({
  featureId,
  initialView,
  onChange,
  themeColors,
  themeStyle,
  t,
}: {
  featureId: string;
  initialView: CalendarViews;
  onChange: (view: CalendarViews) => void;
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
  t: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [currentView, setCurrentView] = useState<CalendarViews>(initialView);
  const domRef = useRef<HTMLDivElement>(null);

  const onDocumentMouseDown = useCallback((event: MouseEvent) => {
    if (event.target === domRef.current || domRef.current?.contains(event.target as Node | null)) {
      return;
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', onDocumentMouseDown, false);
      return () => {
        document.removeEventListener('mousedown', onDocumentMouseDown, false);
      };
    }
  }, [open, onDocumentMouseDown]);

  const onSelectClick = () => {
    setOpen(!open);
  };

  const viewTexts = {
    [CalendarViews.MONTH]: t('tasks.month'),
    [CalendarViews.WEEK]: t('tasks.week'),
    [CalendarViews.DAY]: t('tasks.day'),
  };

  const onViewClick = (view: CalendarViews) => {
    onChange(view);
    setCurrentView(view);
    setOpen(false);
  };

  return (
    <div className="fc-header-select relative z-10 text-xs select-none" ref={domRef}>
      <div
        className={classNames(
          'pl-3 pr-2 py-1 rounded border cursor-pointer flex items-center',
          {
            'hover:bg-neutral-200': !open && !themeColors,
          },
          themeColors && themeStyle
            ? [
                ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_INNER),
                ...getBgClassNames(themeColors, PortalBgType.SURFACE),
                ...getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER]),
              ]
            : 'border-neutral-200'
        )}
        onClick={onSelectClick}
      >
        <span className="fc-header-select-current block">{viewTexts[currentView]}</span>
        <Icon
          icon={IconsMap.ARROW_DOWN}
          className={classNames(
            'w-4 ml-2',
            themeColors ? getTextClassNames(themeColors, PortalTextType.SURFACE_3) : 'text-neutral-500'
          )}
        />
      </div>
      <div
        className={classNames(
          'w-full absolute top-full left-0 rounded border',
          {
            hidden: !open,
          },
          themeColors && themeStyle
            ? [
                ...getBgClassNames(themeColors, PortalBgType.SURFACE),
                ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_INNER),
              ]
            : 'border-neutral-200 bg-white'
        )}
      >
        {Object.keys(viewTexts).map((viewType) => (
          <div
            className={classNames(
              'px-3 py-1 cursor-pointer',
              themeColors
                ? getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER])
                : 'hover:bg-neutral-200'
            )}
            onClick={() => onViewClick(viewType as CalendarViews)}
            key={featureId + '-' + viewType + '-calendar-view'}
          >
            {viewTexts[viewType as CalendarViews]}
          </div>
        ))}
      </div>
    </div>
  );
}
